import { useState } from "react";
import type { FAQContent } from "../../types/page-content";

interface Props {
  content: FAQContent;
  onEstimateClick: () => void;
  sectionNumber?: string;
}

export default function FAQSection({ content, onEstimateClick, sectionNumber = "08" }: Props) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section className="bg-[#161616] py-28 lg:py-40 relative overflow-hidden">
      <div className="absolute right-6 top-10 hidden xl:block">
        <span className="font-heading text-white/[0.03] text-[180px] leading-none select-none">{sectionNumber}</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Left sticky header */}
          <div className="lg:sticky lg:top-28 h-fit">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-0.5 bg-safety-500" />
              <span className="font-body text-safety-500 text-xs uppercase tracking-widest2 font-700">{content.eyebrow}</span>
            </div>
            <h2 className="font-heading text-white text-5xl lg:text-7xl tracking-display leading-none mb-8">
              {content.heading}
            </h2>
            <p className="font-body text-asphalt-300 text-sm leading-relaxed mb-8">
              Can&apos;t find your answer? Talk to our team directly — no pressure, just honest answers.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={onEstimateClick}
                className="flex items-center justify-center gap-2 bg-safety-500 text-asphalt-950 font-body font-700 text-xs uppercase tracking-widest px-6 py-4 hover:bg-safety-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line" />
                Get Free Estimate
              </button>
              <a
                href="tel:5558007283"
                className="flex items-center justify-center gap-2 border border-asphalt-500 text-asphalt-200 font-body font-700 text-xs uppercase tracking-widest px-6 py-4 hover:border-safety-500 hover:text-safety-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill" />
                (555) 800-7283
              </a>
            </div>
          </div>

          {/* Right accordion */}
          <div className="lg:col-span-2 flex flex-col gap-px bg-asphalt-600">
            {content.items.map((faq, idx) => (
              <div key={idx} className="bg-[#191919]">
                <button
                  onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                  className="w-full flex items-start justify-between gap-4 px-8 py-6 text-left cursor-pointer group"
                >
                  <span className="font-heading text-white text-xl tracking-display group-hover:text-safety-500 transition-colors leading-snug">
                    {faq.q}
                  </span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 border border-asphalt-500 group-hover:border-safety-500 transition-colors">
                    <i
                      className={`${openIdx === idx ? "ri-subtract-line" : "ri-add-line"} text-safety-500 text-sm`}
                    />
                  </div>
                </button>
                {openIdx === idx && (
                  <div className="px-8 pb-6 border-t border-asphalt-700">
                    <p className="font-body text-asphalt-300 text-sm leading-relaxed pt-5">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
